import type { ImgHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

interface ResponsiveImageSource {
  media?: string;
  sizes?: string;
  srcSet: string;
  type?: string;
}

interface ResponsiveImageProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "className"
> {
  containerClassName?: string;
  imgClassName?: string;
  sources?: ResponsiveImageSource[];
}

/**
 * Standardized image wrapper for stable sizing, async decoding, and responsive
 * source delivery across section-driven pages.
 */
export function ResponsiveImage({
  alt,
  containerClassName,
  decoding = "async",
  fetchPriority,
  height,
  imgClassName,
  loading = "lazy",
  sizes,
  sources,
  src,
  srcSet,
  width,
  ...imgProps
}: ResponsiveImageProps): JSX.Element {
  return (
    <picture
      className={cn(
        "block overflow-hidden bg-[hsl(var(--surface-muted))]",
        containerClassName,
      )}
    >
      {sources?.map((source) => (
        <source
          key={`${source.type ?? "default"}-${source.media ?? "all"}-${source.srcSet}`}
          media={source.media}
          sizes={source.sizes}
          srcSet={source.srcSet}
          type={source.type}
        />
      ))}
      <img
        alt={alt}
        className={cn(
          "block h-full w-full object-cover [transform:translateZ(0)]",
          imgClassName,
        )}
        decoding={decoding}
        fetchPriority={fetchPriority}
        height={height}
        loading={loading}
        sizes={sizes}
        src={src}
        srcSet={srcSet}
        width={width}
        {...imgProps}
      />
    </picture>
  );
}
